/* eslint-disable consistent-return */
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FieldInputProps, FieldMetaProps } from 'formik';
import { InputAdornment, TextField } from '@mui/material';
import { ErrorOutline } from '@mui/icons-material';
import {
  InvalidMoneyFieldCharacters,
  InvalidMoneyFieldOnPaste,
  setFieldOnSessionStorage,
  testDecimalFieldFormat,
} from '../../helpers/helpers';
import {
  CHARS_AFTER_DECIMAL_POINT,
  CHARS_BEFORE_DECIMAL_POINT,
  DEFAULT_STEP,
} from '../../constants';
import { MoneyFieldProps } from './types';

type InnerMoneyFieldProps = MoneyFieldProps & {
  field: FieldInputProps<number>;
  meta: FieldMetaProps<number>;
};

const InnerMoneyField = (props: InnerMoneyFieldProps) => {
  const {
    label,
    id,
    currencySymbol,
    formName,
    objectId = '',
    autoComplete = 'off',
    required = false,
    smallPadding = false,
    step = DEFAULT_STEP,
    error,
    clearError,
    helperText = '',
    onChange: userOnChange = () => undefined,
    meta,
    field,
    fieldDefaultValue,
  } = props;

  const { t } = useTranslation();
  const optionalHint = ` (${t('components.productForm.optionalHint')})`;

  const changeField = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (
      e.target.defaultValue.length < e.target.value.length &&
      !testDecimalFieldFormat(e.target.value, CHARS_BEFORE_DECIMAL_POINT, CHARS_AFTER_DECIMAL_POINT)
    ) {
      return false;
    }

    clearError?.(id);
    if (formName) {
      setFieldOnSessionStorage(formName, objectId, field.name, e.currentTarget.value);
    }

    field.onChange(e);
    await userOnChange(e);
  };

  const selectAllContentWhenValueIsDefault = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      if (event.target.valueAsNumber === fieldDefaultValue) {
        event.target.select();
      }
    },
    [fieldDefaultValue]
  );

  return (
    <TextField
      id={id}
      label={`${label}${!required ? optionalHint : ''}`}
      fullWidth
      autoComplete={autoComplete}
      required={required}
      type="number"
      size={smallPadding ? 'small' : 'medium'}
      onKeyDown={(event) =>
        InvalidMoneyFieldCharacters.includes(event.key) && event.preventDefault()
      }
      onPaste={(event) =>
        InvalidMoneyFieldOnPaste(event.clipboardData.getData('text')) && event.preventDefault()
      }
      helperText={
        (Boolean(error) && error) ||
        (meta.touched && meta.error && t(meta.error, { field: label })) ||
        helperText
      }
      {...field}
      onChange={changeField}
      onFocus={selectAllContentWhenValueIsDefault}
      error={Boolean(error) || (meta.touched && Boolean(meta.error))}
      InputProps={{
        inputProps: {
          min: 0,
          step,
          style: { textAlign: 'start' },
          'aria-label': label,
          inputMode: 'decimal',
        },
        startAdornment: (
          <InputAdornment position="start">
            <span>{currencySymbol}</span>
          </InputAdornment>
        ),
        endAdornment: meta.touched && Boolean(meta.error) && (
          <InputAdornment position="end">
            <ErrorOutline color="error" />
          </InputAdornment>
        ),
      }}
      sx={(theme) => ({
        marginBottom: { xs: '16px', sm: '24px' },
        textAlign: 'end',
        'input::-webkit-outer-spin-button, input::-webkit-inner-spin-button': {
          WebkitAppearance: 'none',
          margin: 0,
        },
        'input[type=number]': {
          MozAppearance: 'textfield',
        },
        'input &::placeholder': {
          color: theme.palette.text.primary,
        },
      })}
    />
  );
};

export default InnerMoneyField;
